import type { ReactNode } from 'react';
import { cn } from '@/utils/cn';

interface FullscreenPageProps {
  titulo?: string;
  subtitulo?: string;
  /** Acción opcional del header (botón, switch). */
  accion?: ReactNode;
  children: ReactNode;
  className?: string;
  /** Si true, el body tiene scroll interno bounded. Default true. */
  bodyScroll?: boolean;
}

/**
 * Layout estándar de página de juego:
 *  - Ocupa el alto disponible (sin desbordar la ventana).
 *  - Header fijo arriba, contenido con scroll interno contenido.
 *  - Padding fluido por viewport.
 */
export function FullscreenPage({
  titulo,
  subtitulo,
  accion,
  children,
  className,
  bodyScroll = true,
}: FullscreenPageProps) {
  return (
    <div
      className={cn(
        'h-full w-full flex flex-col min-h-0 min-w-0',
        'px-[clamp(1rem,3vmin,2.5rem)] py-[clamp(0.75rem,2vmin,1.5rem)]',
        className,
      )}
    >
      {(titulo || subtitulo || accion) && (
        <header className="shrink-0 flex items-end justify-between gap-4 mb-[clamp(0.5rem,2vmin,1.5rem)]">
          <div className="min-w-0">
            {titulo && (
              <h1 className="fluid-2xl text-display text-ash-100 tracking-wider truncate">
                {titulo}
              </h1>
            )}
            {subtitulo && (
              <p className="fluid-xs text-ash-400 font-ui mt-1 truncate">{subtitulo}</p>
            )}
          </div>
          {accion && <div className="shrink-0">{accion}</div>}
        </header>
      )}
      <div
        className={cn(
          'flex-1 min-h-0 min-w-0',
          bodyScroll && 'scroll-y',
        )}
      >
        {children}
      </div>
    </div>
  );
}
